import React, { useState, useEffect } from 'react';
import { toast } from "sonner";
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Language, LANGUAGE_NAMES } from '../../../shared/types';
import { getStoredAdminAuthToken } from '@/lib/admin/auth';
import { useAppStore } from '@/hooks/useAppStore';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { MicVAD, RealTimeVADOptions, getDefaultRealTimeVADOptions } from '@ricky0123/vad-web';
import { VADEagerness } from '@/types';
import { VAD_EAGERNESS_PRESETS } from '@/components/layout/VADProvider';
import { cn } from '@/lib/utils';

// Get admin auth token from localStorage
const getAuthToken = () => {
    return getStoredAdminAuthToken().token || '';
};

// Get admin user info
const getAdminUser = () => {
    return getStoredAdminAuthToken().user;
};

type Tab = 'translation' | 'transcription';

interface TranscriptionVersion {
    text: string;
    promptUsed: string;
    timestamp: number;
}

interface TranscriptionItem {
    id: string;
    timestamp: number;
    file: File;
    text: string;
    detectedLanguage?: string;
    promptUsed: string;
    source: 'manual' | 'vad';
    previousVersions: TranscriptionVersion[];
}

export const AdminPlaygroundPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<Tab>('translation');
    const adminLogout = useAppStore(state => state.adminLogout);

    // Translation state
    const [sourceText, setSourceText] = useState('');
    const [targetLang, setTargetLang] = useState<Language>('ja');
    const [context, setContext] = useState('');
    const [customPrompt, setCustomPrompt] = useState(`Translate the below line into {targetLangName}. Return only the translated text and nothing else. If there are obvious ASR errors like infinite repetitions, ignore them. Here is some prior context from the conversation to improve translation quality.
****previous context****
{context}
****end previous context****

****LINE TO TRANSLATE****
{text}
********`);
    const [translationResult, setTranslationResult] = useState('');
    const [translationUsedPrompt, setTranslationUsedPrompt] = useState('');
    const [isTranslating, setIsTranslating] = useState(false);

    // Transcription state
    const [audioFile, setAudioFile] = useState<File | null>(null);
    const [transcriptionPrompt, setTranscriptionPrompt] = useState('Transcribe the Japanese audio clip into text. Here are hotwords that may appear in the text: {keywords}');
    const [transcriptionKeywords, setTranscriptionKeywords] = useState('Shisa AI, Jia Shen, Jian Shen');
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [isReporting, setIsReporting] = useState<Record<string, boolean>>({});
    const [transcriptionHistory, setTranscriptionHistory] = useState<TranscriptionItem[]>([]);

    // Recording state
    const [isRecording, setIsRecording] = useState(false);
    const [recorderInstance, setRecorderInstance] = useState<any>(null);

    const [recordingTime, setRecordingTime] = useState(0);
    const [recordingInterval, setRecordingInterval] = useState<NodeJS.Timeout | null>(null);

    // VAD State
    const [isVADMode, setIsVADMode] = useState(false);
    const [vadEagerness, setVADEagerness] = useState<VADEagerness>('medium');
    const [vadStatus, setVADStatus] = useState<string>('Idle');
    const [vad, setVAD] = useState<MicVAD | null>(null);

    // Hooks
    const { recorder, isInitializing } = useAudioRecorder();

    // Sample contexts - matching TranslateSession format
    const SAMPLE_CONTEXTS = {
        sample1: `Speaker A: Hello, how are you today?
Speaker B: I'm doing well, thank you! How about you?
Speaker A: Great! I wanted to discuss the project timeline.
Speaker B: Sure, let's go over the milestones.
Speaker A: We need to finalize the design by next week.`,
        sample2: `Speaker A: おはようございます。今日の会議について話したいです。
Speaker B: はい、もちろんです。何時に始まりますか？
Speaker A: 午後2時からです。
Speaker B: わかりました。準備しておきます。
Speaker A: ありがとうございます。`,
        sample3: `Speaker A: I'd like to order a coffee, please.
Speaker B: Sure! What size would you like?
Speaker A: A medium latte with oat milk, please.
Speaker B: Coming right up! Anything else?
Speaker A: No, that's all. Thank you!`
    };

    // History State


    // VAD Effect
    useEffect(() => {
        if (!isVADMode || isInitializing || !recorder?.encodeAudio) {
            if (vad) {
                vad.destroy();
                setVAD(null);
                setVADStatus('Idle');
            }
            return;
        }

        let myvad: MicVAD | null = null;
        let startedAt = 0;
        let audioBuffers: Float32Array[] = [];
        let totalAudioDuration = 0;
        let lastSpeechEndTime = 0;
        let isSpeaking = false;
        const vadConfig = VAD_EAGERNESS_PRESETS[vadEagerness];

        const sendAccumulatedAudio = async (reason: string) => {
            if (audioBuffers.length === 0) return;

            console.log(`VAD Play: Sending audio (${reason}) - Duration: ${totalAudioDuration}ms`);
            setVADStatus(`Transcribing...`);

            try {
                // Concatenate audio
                const totalLength = audioBuffers.reduce((sum, buf) => sum + buf.length, 0);
                const concatenated = new Float32Array(totalLength);
                let offset = 0;
                for (const buffer of audioBuffers) {
                    concatenated.set(buffer, offset);
                    offset += buffer.length;
                }

                // Encode
                const opusBlob = await recorder.encodeAudio(concatenated);

                // Transcribe immediately
                // We create a File object to reuse handleTranscribe logic cleanly
                const file = new File([opusBlob], `vad-${Date.now()}.ogg`, { type: 'audio/ogg; codecs=opus' });
                // We call handleTranscribe directly with the blob/file
                handleTranscribe(file, 'vad');

            } catch (error) {
                console.error("VAD Encoding error:", error);
                setVADStatus("Encoding Error");
            } finally {
                audioBuffers = [];
                totalAudioDuration = 0;
                lastSpeechEndTime = 0;
                setVADStatus("Listening...");
            }
        };

        const vadOptions: RealTimeVADOptions = {
            ...getDefaultRealTimeVADOptions("v5"),
            model: "v5",
            frameSamples: 512,
            positiveSpeechThreshold: 0.5,
            negativeSpeechThreshold: 0.35,
            redemptionFrames: 8,
            preSpeechPadFrames: 3,
            minSpeechFrames: 12,
            submitUserSpeechOnPause: true,

            onFrameProcessed() {
                if (startedAt && (Date.now() - startedAt) > vadConfig.maxAudioDuration) {
                    myvad?.pause();
                    myvad?.start();
                    startedAt = 0;
                }

                if (!isSpeaking && audioBuffers.length > 0 && lastSpeechEndTime > 0) {
                    const silenceDuration = Date.now() - lastSpeechEndTime;
                    if (silenceDuration >= vadConfig.maxSilenceDuration) {
                        lastSpeechEndTime = 0;
                        sendAccumulatedAudio('MAX_SILENCE_EXCEEDED');
                    }
                }
            },

            onSpeechEnd: async (audio: Float32Array) => {
                const speechDuration = Date.now() - startedAt;
                console.log(`VAD Play: Speech End ${speechDuration}ms`);
                setVADStatus("Processing Speech...");
                startedAt = 0;
                isSpeaking = false;

                audioBuffers.push(audio);
                const audioDurationMs = (audio.length / 16000) * 1000;
                totalAudioDuration += audioDurationMs;
                lastSpeechEndTime = Date.now();

                if (totalAudioDuration >= vadConfig.minAudioDuration) {
                    // Often send immediately if min duration met
                    await sendAccumulatedAudio('MIN_DURATION_MET');
                } else {
                    setVADStatus("Accumulating...");
                }
            },

            onSpeechStart: () => {
                console.log("VAD Play: Speech Start");
                setVADStatus("Speaking...");
                startedAt = Date.now();
                isSpeaking = true;
            },

            onVADMisfire: () => {
                console.log("VAD Play: Misfire");
                setVADStatus("Listening...");
                isSpeaking = false;
            }
        };

        const setup = async () => {
            try {
                setVADStatus("Initializing VAD...");
                myvad = await MicVAD.new(vadOptions);
                myvad.start();
                setVAD(myvad);
                setVADStatus("Listening...");
            } catch (e) {
                console.error("Failed to start VAD", e);
                setVADStatus("VAD Error");
            }
        };

        setup();

        return () => {
            if (myvad) {
                myvad.destroy();
            }
        };
    }, [isVADMode, isInitializing, recorder, vadEagerness]);


    // Generate prompt preview
    const getPromptPreview = () => {
        let prompt = customPrompt
            .replace('{targetLangName}', LANGUAGE_NAMES[targetLang])
            .replace('{text}', sourceText || '[Your text here]');

        if (context && customPrompt.includes('{context}')) {
            prompt = prompt.replace('{context}', context);
        }

        return prompt;
    };

    // Generate transcription prompt preview
    const getTranscriptionPromptPreview = () => {
        return transcriptionPrompt.replace('{keywords}', transcriptionKeywords || '[Keywords here]');
    };

    const handleTranslate = async () => {
        if (!sourceText.trim()) return;

        setIsTranslating(true);
        setTranslationResult('');

        try {
            const response = await fetch('/api/admin/test-translation', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getAuthToken()}`,
                },
                body: JSON.stringify({
                    text: sourceText,
                    targetLang,
                    context: context || undefined,
                    customPrompt: customPrompt !== '' ? customPrompt : undefined,
                }),
            });

            if (!response.ok) {
                throw new Error('Translation failed');
            }

            const data = await response.json();
            setTranslationResult(data.translation || 'No translation result');
            setTranslationUsedPrompt(data.prompt || '');
        } catch (error) {
            console.error('Translation error:', error);
            setTranslationResult('Error: Translation failed');
            setTranslationUsedPrompt('');
        } finally {
            setIsTranslating(false);
        }
    };

    const handleAudioUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setAudioFile(file);
        }
    };

    const startManualRecording = async () => {
        if (!(window as any).Recorder) {
            alert("Audio engine not ready. Please wait.");
            return;
        }

        // Standard getUserMedia
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
            const context = new AudioContextClass({ sampleRate: 16000 });
            if (context.state === 'suspended' || context.state === 'interrupted') await context.resume();

            const source = context.createMediaStreamSource(stream);
            const config = {
                numberOfChannels: 1,
                encoderSampleRate: 16000,
                encoderFrameSize: 20,
                maxFramesPerPage: 40,
                encoderComplexity: 6,
                encoderApplication: 2048,
                originalSampleRate: context.sampleRate,
                streamPages: true,
                sourceNode: source,
            };

            const localRecorder = new (window as any).Recorder(config);
            const chunks: Uint8Array[] = [];
            localRecorder.ondataavailable = (typedArray: Uint8Array) => chunks.push(typedArray);
            localRecorder.onstop = () => {
                const blob = new Blob(chunks as any, { type: 'audio/ogg; codecs=opus' });
                const file = new File([blob], `manual-recording-${Date.now()}.ogg`, { type: 'audio/ogg; codecs=opus' });
                setAudioFile(file);
                stream.getTracks().forEach(track => track.stop());
                context.close();
            };

            localRecorder.start();
            setRecorderInstance(localRecorder);
            setIsRecording(true);
            setRecordingTime(0);

            const interval = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
            setRecordingInterval(interval);

        } catch (e) {
            console.error(e);
            alert("Could not start recording");
        }
    };

    const stopManualRecording = () => {
        if (recorderInstance && isRecording) {
            recorderInstance.stop();
            setIsRecording(false);
            setRecorderInstance(null);
            if (recordingInterval) {
                clearInterval(recordingInterval);
                setRecordingInterval(null);
            }
        }
    };


    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleTranscribe = async (fileOverride?: File, source: 'manual' | 'vad' = 'manual', retryItemId?: string) => {
        const fileToUse = fileOverride || audioFile;
        if (!fileToUse) return;

        setIsTranscribing(true);

        // Prepare prompt
        const finalPrompt = transcriptionPrompt.replace('{keywords}', transcriptionKeywords);

        try {
            const formData = new FormData();
            formData.append('audio', fileToUse);
            if (finalPrompt) {
                formData.append('prompt', finalPrompt);
            }

            const response = await fetch('/api/admin/test-transcription', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${getAuthToken()}`,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Transcription failed');
            }

            const data = await response.json();
            const text = data.text || 'No transcription result';
            const detectedLanguage = data.language;

            if (retryItemId) {
                // Update existing item
                setTranscriptionHistory(prev => prev.map(item => {
                    if (item.id === retryItemId) {
                        return {
                            ...item,
                            text: text,
                            detectedLanguage: detectedLanguage,
                            promptUsed: finalPrompt,
                            previousVersions: [
                                ...item.previousVersions,
                                {
                                    text: item.text,
                                    promptUsed: item.promptUsed,
                                    timestamp: Date.now()
                                }
                            ]
                        };
                    }
                    return item;
                }));
            } else {
                // Add new item
                const newItem: TranscriptionItem = {
                    id: Date.now().toString(),
                    timestamp: Date.now(),
                    file: fileToUse,
                    text: text,
                    detectedLanguage: detectedLanguage,
                    promptUsed: finalPrompt,
                    source: source,
                    previousVersions: []
                };
                setTranscriptionHistory(prev => [newItem, ...prev]);
            }
        } catch (error) {
            console.error('Transcription error:', error);
            alert('Transcription failed');
        } finally {
            setIsTranscribing(false);
        }
    };

    const handleReport = async (item: TranscriptionItem) => {
        const expectedText = prompt('Expected Text (What should it have been)?:', item.text);
        if (expectedText === null) return;

        const comments = prompt('Additional comments (A brief of the kind of issue)?: ');
        if (comments === null) return;

        setIsReporting(prev => ({ ...prev, [item.id]: true }));
        try {
            const rowData = [
                item.file.name,
                item.text, // Transcribed Text
                expectedText, // Expected Text
                item.promptUsed, // Prompt
                comments, // Additional comments
                getAdminUser()?.email || 'unknown', // Reported By
                new Date().toISOString(), // Reported At,
                item.detectedLanguage,
            ];

            const formData = new FormData();
            formData.append('audio', item.file);
            formData.append('rowData', JSON.stringify(rowData));

            const response = await fetch('/api/admin/report-transcription', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${getAuthToken()}`,
                },
                body: formData,
            });

            if (response.ok) {
                alert('Reported successfully!');
            } else {
                const data = await response.json();
                alert(`Failed to report: ${data.error || 'Unknown error'} `);
            }
        } catch (error) {
            console.error('Reporting error', error);
            alert('Failed to report');
        } finally {
            setIsReporting(prev => ({ ...prev, [item.id]: false }));
        }
    };

    return (
        <div className="bg-white h-screen w-full overflow-y-auto text-black">
            {/* Header */}
            <div className="border-b border-gray-300 px-6 py-4 flex items-center justify-between">
                <h1 className="text-2xl font-bold m-0">Prompt Playground</h1>
                <div className="flex items-center gap-4">
                    <div className="text-sm text-gray-600">
                        Logged in as: <span className="font-medium">{getAdminUser()?.email || 'Admin'}</span>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                            adminLogout();
                            window.location.href = '/admin-login';
                        }}
                    >
                        Logout
                    </Button>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-300 px-6 flex gap-2">
                <Button
                    onClick={() => setActiveTab('translation')}
                    variant="ghost"
                    className={`px-6 py-3 border-b-2 rounded-none transition-colors cursor-pointer text-sm font-medium hover:bg-transparent ${activeTab === 'translation'
                        ? 'border-b-black text-black'
                        : 'border-b-transparent text-gray-500 hover:text-gray-700 hover:border-b-gray-300'
                        }`}
                >
                    Translation Testing
                </Button>
                <Button
                    onClick={() => setActiveTab('transcription')}
                    variant="ghost"
                    className={`px-6 py-3 border-b-2 rounded-none transition-colors cursor-pointer text-sm font-medium hover:bg-transparent ${activeTab === 'transcription'
                        ? 'border-b-black text-black'
                        : 'border-b-transparent text-gray-500 hover:text-gray-700 hover:border-b-gray-300'
                        }`}
                >
                    Transcription Testing
                </Button>
            </div>

            {/* Content */}
            <div className="p-6">
                {activeTab === 'translation' && (
                    <div className="grid grid-cols-2 gap-6">
                        {/* Left Column - Input */}
                        <div>
                            <h2 className="text-lg font-bold mb-4">Input</h2>

                            {/* Language Selection */}
                            <div className="mb-4">
                                <label className="block mb-2 font-medium">
                                    Target Language
                                </label>
                                <select
                                    value={targetLang}
                                    onChange={(e) => setTargetLang(e.target.value as Language)}
                                    className="w-full p-2 border border-gray-300 rounded"
                                >
                                    <option value="en">English</option>
                                    <option value="ja">Japanese</option>
                                    <option value="zh">Chinese</option>
                                </select>
                            </div>

                            {/* Source Text */}
                            <div className="mb-4">
                                <label className="block mb-2 font-medium">
                                    Text to Translate
                                </label>
                                <Textarea
                                    value={sourceText}
                                    onChange={(e) => setSourceText(e.target.value)}
                                    placeholder="Enter text to translate..."
                                    rows={4}
                                    className="w-full border border-gray-300"
                                />
                            </div>

                            {/* Context */}
                            <div className="mb-4">
                                <label className="block mb-2 font-medium">
                                    Context (Optional)
                                </label>
                                <Textarea
                                    value={context}
                                    onChange={(e) => setContext(e.target.value)}
                                    placeholder="Previous conversation context..."
                                    rows={3}
                                    className="w-full border border-gray-300"
                                />
                                <div className="flex gap-2 mt-2">
                                    <Button
                                        onClick={() => setContext(SAMPLE_CONTEXTS.sample1)}
                                        variant="outline"
                                        size="sm"
                                        className="cursor-pointer "
                                    >
                                        Sample 1
                                    </Button>
                                    <Button
                                        onClick={() => setContext(SAMPLE_CONTEXTS.sample2)}
                                        variant="outline"
                                        size="sm"
                                        className="cursor-pointer "
                                    >
                                        Sample 2
                                    </Button>
                                    <Button
                                        onClick={() => setContext(SAMPLE_CONTEXTS.sample3)}
                                        variant="outline"
                                        size="sm"
                                        className="cursor-pointer "
                                    >
                                        Sample 3
                                    </Button>
                                </div>
                            </div>

                            {/* Custom Prompt */}
                            <div className="mb-4">
                                <label className="block mb-2 font-medium">
                                    Custom Prompt Template
                                </label>
                                <Textarea
                                    value={customPrompt}
                                    onChange={(e) => setCustomPrompt(e.target.value)}
                                    placeholder="Use {targetLangName}, {text}, {context} as variables"
                                    rows={6}
                                    className="w-full border border-gray-300 font-mono text-xs"
                                />
                                <p className="text-xs text-gray-600 mt-1">
                                    Variables: {'{targetLangName}'}, {'{text}'}, {'{context}'}
                                </p>
                            </div>

                            <Button
                                onClick={handleTranslate}
                                disabled={isTranslating || !sourceText.trim()}
                                variant="default"
                                className="w-full cursor-pointer"
                            >
                                {isTranslating ? 'Translating...' : 'Translate'}
                            </Button>
                        </div>

                        {/* Right Column - Output */}
                        <div>
                            <h2 className="text-lg font-bold mb-4">Output</h2>

                            {/* Prompt Preview */}
                            <div className="mb-4">
                                <label className="block mb-2 font-medium">
                                    Prompt Preview
                                </label>
                                <pre className="p-3 bg-gray-100 border border-gray-300 rounded text-xs whitespace-pre-wrap break-words max-h-[200px] overflow-auto">
                                    {getPromptPreview()}
                                </pre>
                                <Button
                                    onClick={() => {
                                        navigator.clipboard.writeText(getPromptPreview());
                                        toast.success('Prompt copied to clipboard');
                                    }}
                                    variant="outline"
                                    size="sm"
                                    className='mt-2 cursor-pointer '
                                >
                                    Copy Prompt
                                </Button>
                            </div>

                            {/* Translation Result */}
                            <div>
                                {translationUsedPrompt && (
                                    <div className="mb-4">
                                        <label className="block mb-2 font-medium">
                                            Actual Prompt Used
                                        </label>
                                        <pre className="p-3 bg-gray-100 border border-gray-300 rounded text-xs whitespace-pre-wrap break-words max-h-[200px] overflow-auto">
                                            {translationUsedPrompt}
                                        </pre>
                                    </div>
                                )}
                                <label className="block mb-2 font-medium">
                                    Translation Result
                                </label>
                                <div className="p-3 bg-gray-100 border border-gray-300 rounded min-h-[100px] whitespace-pre-wrap break-words">
                                    {translationResult || 'Translation will appear here...'}
                                </div>
                                {translationResult && (
                                    <Button
                                        onClick={() => {
                                            navigator.clipboard.writeText(translationResult);
                                            toast.success('Translation result copied to clipboard');
                                        }}
                                        variant="outline"
                                        size="sm"
                                        className='mt-2 cursor-pointer '
                                    >
                                        Copy Result
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'transcription' && (
                    <div className="grid grid-cols-2 gap-6">
                        {/* Left Column - Input */}
                        <div>
                            <h2 className="text-lg font-bold mb-4">Input</h2>

                            {/* Audio Actions Row */}
                            <div className="mb-4">
                                <label className="block mb-2 font-medium">
                                    Audio Input
                                </label>
                                <input
                                    id="audio-upload"
                                    type="file"
                                    accept="audio/*"
                                    onChange={handleAudioUpload}
                                    className="hidden"
                                />
                                <div className="flex flex-wrap gap-2 mt-2 items-center">
                                    <Button
                                        onClick={() => document.getElementById('audio-upload')?.click()}
                                        variant="outline"
                                        size="sm"
                                        disabled={isRecording || isVADMode}
                                        className="cursor-pointer"
                                    >
                                        Upload File
                                    </Button>
                                    <Button
                                        onClick={isRecording ? stopManualRecording : startManualRecording}
                                        size="sm"
                                        variant={isRecording ? "destructive" : "outline"}
                                        disabled={isVADMode}
                                        className={cn("cursor-pointer")}
                                    >
                                        {isRecording ? `Stop Recording(${formatTime(recordingTime)})` : 'Record'}
                                    </Button>

                                    <div className="h-6 w-px bg-gray-300 mx-2" />

                                    <Button
                                        onClick={() => setIsVADMode(!isVADMode)}
                                        variant="default"
                                        disabled={isRecording}
                                        size="sm"
                                        className="cursor-pointer"
                                    >
                                        {isVADMode ? 'Stop VAD' : 'Start VAD'}
                                    </Button>

                                    {isVADMode && (
                                        <div className="flex items-center gap-2 ml-2 bg-blue-50 px-1 rounded border border-blue-100 text-sm">
                                            <label className="font-medium text-blue-900 text-sm">Eagerness:</label>
                                            <select
                                                value={vadEagerness}
                                                onChange={(e) => setVADEagerness(e.target.value as VADEagerness)}
                                                className="p-1 border rounded text-xs"
                                            >
                                                <option value="low">Low (Patient)</option>
                                                <option value="medium">Medium</option>
                                                <option value="high">High (Fast)</option>
                                            </select>
                                            <div className="w-px h-4 bg-blue-200 mx-1"></div>
                                            <span className="font-bold font-mono text-blue-700 text-xs">{vadStatus}</span>
                                        </div>
                                    )}
                                </div>

                                {audioFile && (
                                    <p className="text-sm text-gray-600 mt-2">
                                        Selected: {audioFile.name} ({(audioFile.size / 1024 / 1024).toFixed(2)} MB)
                                    </p>
                                )}
                            </div>

                            {/* Audio Player */}
                            {audioFile && (
                                <div className="mb-4">
                                    <label className="block mb-2 font-medium">
                                        Audio Preview
                                    </label>
                                    <audio
                                        controls
                                        src={URL.createObjectURL(audioFile)}
                                        className="w-full"
                                    />
                                </div>
                            )}

                            {/* Keywords */}
                            <div className="mb-4">
                                <label className="block mb-2 font-medium">
                                    Keywords
                                </label>
                                <Textarea
                                    value={transcriptionKeywords}
                                    onChange={(e) => setTranscriptionKeywords(e.target.value)}
                                    placeholder="Names, terms, or context words..."
                                    rows={2}
                                    className="w-full border border-gray-300"
                                />
                            </div>

                            {/* Transcription Prompt Template */}
                            <div className="mb-4">
                                <label className="block mb-2 font-medium">
                                    Transcription Prompt Template
                                </label>
                                <Textarea
                                    value={transcriptionPrompt}
                                    onChange={(e) => setTranscriptionPrompt(e.target.value)}
                                    placeholder="Use {keywords} as variable"
                                    rows={3}
                                    className="w-full border border-gray-300 font-mono text-xs"
                                />
                                <p className="text-xs text-gray-600 mt-1">
                                    Variable: {'{keywords}'}
                                </p>
                            </div>

                            <Button
                                onClick={() => handleTranscribe()}
                                variant="default"
                                disabled={isTranscribing || (!audioFile && !isVADMode)}
                                className="w-full mb-8 cursor-pointer"
                            >
                                {isTranscribing ? 'Transcribing...' : 'Transcribe'}
                            </Button>
                        </div>

                        {/* Right Column - Output */}
                        <div>
                            <h2 className="text-lg font-bold mb-4">Output</h2>

                            {/* Prompt Preview */}
                            <div className="mb-4">
                                <label className="block mb-2 font-medium">
                                    Prompt Preview
                                </label>
                                <pre className="p-3 bg-gray-100 border border-gray-300 rounded text-xs whitespace-pre-wrap break-words">
                                    {getTranscriptionPromptPreview()}
                                </pre>
                            </div>

                            {/* Transcription Result History */}
                            <div className="space-y-6">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-bold">Transcription History</h3>
                                    {transcriptionHistory.length > 0 && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setTranscriptionHistory([])}
                                            className="text-red-500 hover:text-red-700 cursor-pointer"
                                        >
                                            Clear History
                                        </Button>
                                    )}
                                </div>

                                {transcriptionHistory.length === 0 && (
                                    <p className="text-gray-500 italic">No transcriptions yet.</p>
                                )}

                                {transcriptionHistory.map((item) => (
                                    <div key={item.id} className="p-4 border rounded-lg bg-gray-50 shadow-sm">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className={`text-xs px-2 py-1 rounded font-bold uppercase ${item.source === 'vad' ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-700'
                                                        }`}
                                                >
                                                    {item.source}
                                                </span>
                                                {item.detectedLanguage && (
                                                    <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-800 font-bold uppercase">
                                                        {item.detectedLanguage}
                                                    </span>
                                                )}
                                                <span className="text-xs text-gray-500">
                                                    {new Date(item.timestamp).toLocaleTimeString()}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="mb-2 flex items-center gap-2">
                                            <audio controls src={URL.createObjectURL(item.file)} className="w-full h-8" />
                                            <Button
                                                onClick={() => handleTranscribe(item.file, 'manual', item.id)}
                                                disabled={isTranscribing}
                                                variant="outline"
                                                size="sm"
                                                className="h-8 cursor-pointer hover:bg-gray-100 transition-colors"
                                            >
                                                Retry
                                            </Button>
                                            <Button
                                                onClick={() => handleReport(item)}
                                                variant="secondary"
                                                size="sm"
                                                disabled={isReporting[item.id]}
                                                className="h-8 bg-green-100 hover:bg-green-200 text-green-800 border-green-200 cursor-pointer transition-colors"
                                            >
                                                {isReporting[item.id] ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Report'}
                                            </Button>
                                        </div>

                                        {/* Text Result */}
                                        <div className="mb-2 flex gap-2 flex-col">
                                            <div className="p-2 bg-white border border-gray-200 rounded min-h-[40px] whitespace-pre-wrap text-sm">
                                                {item.text}
                                            </div>
                                            <Button
                                                onClick={() => {

                                                    navigator.clipboard.writeText(item.text)
                                                    toast.success('Result copied to clipboard');
                                                }}
                                                variant="outline"
                                                size="sm"
                                                className="text-xs h-6 cursor-pointer px-2"
                                            >
                                                Copy Result
                                            </Button>
                                        </div>

                                        {/* Prompt used */}
                                        <div className='flex gap-2 mb-2 flex-col'>

                                            <details className="text-xs text-gray-500 cursor-pointer">
                                                <summary>View Prompt Used</summary>
                                                <div className="flex justify-between  gap-2">
                                                    <pre className="mt-1 p-2 bg-gray-100 rounded whitespace-pre-wrap">{item.promptUsed}</pre>
                                                    <Button
                                                        onClick={() => {

                                                            navigator.clipboard.writeText(item.promptUsed)
                                                            toast.success('Prompt copied to clipboard');
                                                        }}
                                                        variant="secondary"
                                                        size="xs"
                                                        className="text-xs h-6 cursor-pointer inline"
                                                    >
                                                        Copy Prompt
                                                    </Button>
                                                </div>
                                            </details>
                                        </div>

                                        {/* Previous Versions */}
                                        {item.previousVersions && item.previousVersions.length > 0 && (
                                            <div className="mt-4 pt-4 border-t border-gray-200">
                                                <p className="text-xs font-bold text-gray-500 mb-2">Previous Versions</p>
                                                <div className="space-y-3">
                                                    {item.previousVersions.map((version, idx) => (
                                                        <div key={idx} className="bg-gray-100 p-2 rounded text-xs opacity-75">
                                                            <p className="whitespace-pre-wrap">{version.text}</p>
                                                            <details className="mt-1 text-gray-500 cursor-pointer">
                                                                <summary className="text-[10px]">View Prompt</summary>
                                                                <pre className="mt-1 p-1 bg-gray-200 rounded whitespace-pre-wrap">{version.promptUsed}</pre>
                                                            </details>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminPlaygroundPage;
